// Pure business logic for the fuel-plan-compliance slice.
//
// Nothing here touches React or the network. This is the "private method" of the
// compliance table: the per-tractor compliance rollup (count compliant stops,
// count non-compliant, derive the compliance percentage). It lived inline in the
// component in two places - the CSV export memo and the table column accessors -
// which duplicated the arithmetic and let a divide-by-zero slip through. Pulling
// it out here removes the duplication and makes the percentage unit-testable with
// no React. Data access lives in api.ts.

import type { TractorCompliance } from "./api";

export interface TractorComplianceSummary {
  tractor: string;
  numberOfFuelStops: number;
  numberOfCompliantFuelStops: number;
  numberOfNonCompliantFuelStops: number;
  /** Whole-number percent of stops that were compliant. 0 when there are no stops. */
  compliancePercent: number;
}

// Roll a tractor's fuel stops up into its compliance summary. A tractor with no
// fuel stops is 0% compliant, not NaN% - the old inline math divided by zero and
// rendered "NaN%" with no color.
export function summarizeTractorCompliance(compliance: TractorCompliance): TractorComplianceSummary {
  const fuelStops = compliance.fuelStops ?? [];
  const numberOfFuelStops = fuelStops.length;
  const numberOfCompliantFuelStops = fuelStops.filter((stop) => stop.compliance === 1).length;
  const numberOfNonCompliantFuelStops = fuelStops.filter((stop) => stop.compliance === 0).length;
  const compliancePercent =
    numberOfFuelStops === 0 ? 0 : Math.round((numberOfCompliantFuelStops / numberOfFuelStops) * 100);

  return {
    tractor: compliance.tractor,
    numberOfFuelStops,
    numberOfCompliantFuelStops,
    numberOfNonCompliantFuelStops,
    compliancePercent,
  };
}
